import { formatCurrency } from '@adornis/accounting/base/api/currencyFormat.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { IBANInput } from '@adornis/formfield/fields/iban-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { computed } from '@lit-labs/preact-signals';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DownloadCard } from '@martinus/styles/client/DownloadCard.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { ErrorState } from '@adornis/components/client/ErrorState.js';
import { TabBar } from '@adornis/components/client/TabBar.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import { MartinusTemporaryActions } from '../../MartinusTemporaryPolicy.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { SPENDER_ROLES } from '../../permissions/roles.js';
import { getSchoolYear } from '../../schoolyear/getSchoolYear.js';
import { Person } from '../../users/entities/person.js';
import { BankDaten } from '../../users/profile/entities/BankDaten.js';
import { QuittungenAPI } from '../api/QuittungenAPI.js';
import { QuittungDownloadType } from '../entities/MartinusAccountingEntry.js';
import { Quittung } from '../entities/Quittung.js';

const groupings = [
  { fieldName: 'schoolyear', name: 'Schuljahr' },
  { fieldName: 'iban', name: 'IBAN' },
  { fieldName: 'schulID', name: 'Schule' },
];

const quittungSelection = {
  _id: 1,
  date: 1,
  amountInMinorUnit: 1,
  openMinorUnit: 1,
  text: 1,
  iban: 1,
  schoolIDs: 1,
  schools: { name: 1 },
  payments: { _id: 1, paymentKind: 1, amountInMinorUnit: 1 },
} as const;

/**
 * Depending on the activeKey, returns the value of a single quittung for which we want to group
 * @param activeKey The key to group by
 * @param quittung The quittung to group
 */
function getGroupKey(activeKey: string, quittung: NarrowedEntity<Quittung, typeof quittungSelection>) {
  switch (activeKey) {
    case 'iban':
      return quittung.iban || 'IBAN unbekannt';
    case 'schulID':
      return quittung.schools?.map(school => school.name).join(', ') || 'Schule undefiniert';
    case 'schoolyear': {
      if (!quittung.date) return 'Schuljahr undefiniert';

      return `Schuljahr ${getSchoolYear({ date: quittung.date })}`;
    }
    default:
      return 'all';
  }
}

const DownloadButton = stateful(
  (props: { userID: string; quittung: NarrowedEntity<Quittung, typeof quittungSelection>; isSpender: boolean }) => {
    // the kind of the money that came in last; a legacy Kontoauszug row has none
    const paymentKind = props.quittung.payments?.findLast(
      payment => (payment?.amountInMinorUnit ?? 0) > 0,
    )?.paymentKind;
    // a Teilzahlung confirms what came in so far — the rest is shown, so it never reads as paid in full
    const open = props.quittung.openMinorUnit ?? 0;

    return DownloadCard({
      content: html`
        <div class="grid grid-cols-[max-content_max-content_max-content_1fr] items-center gap-4">
          <p class="text-base-content">
            Quittung vom
            <span class="font-semibold">
              ${props.quittung.date ? formatCalendarDay(props.quittung.date, 'dd.MM.yyyy') : ''}
            </span>
            für
            <span class="font-semibold">${formatCurrency(props.quittung.amountInMinorUnit)}</span>
            ${paymentKind ? html`per ${paymentKind}` : nothing}
            ${
              open > 0
                ? html`
                    <span class="badge badge-warning badge-sm">
                      Teilzahlung · von ${formatCurrency((props.quittung.amountInMinorUnit ?? 0) + open)} · offen
                      ${formatCurrency(open)}
                    </span>
                  `
                : nothing
            }
          </p>

          <p class="text-sm text-base-content/80">${props.quittung.iban}</p>
          <p class="text-sm text-base-content/80">${props.quittung.text}</p>
        </div>
      `,
      onClick: async () => {
        await guardDialog(async () => {
          const pdf = await QuittungenAPI.generatePDF(
            props.userID,
            props.quittung._id,
            props.isSpender ? QuittungDownloadType.SPENDER_QUITTUNG : QuittungDownloadType.SB_QUITTUNG,
          )(selectionSet(() => AdornisFile, 2));

          const serveLink = pdf?.getServeLink();
          if (!serveLink) {
            throw new Error('Es gab ein Problem beim erstellen des Downloads.');
          }
          await downloadLink(serveLink, pdf?.meta?.fileName ?? 'Quittung');
        });
      },
    });
  },
);

/**
 * We wrap the page in an extra layer as there is a persons subpage which can use the same rendering logic but does not need the header
 */
export const PersonalQuittungenDownloadPage = stateful((props: { userID: Maybe<string> }) => {
  return html` ${PageHeader({ title: 'Persönliche Quittungen' })} ${PersonalQuittungenDownload(props)} `;
});

export const PersonalQuittungenDownload = stateful((props: { userID: Maybe<string> }) => {
  const { value: user, loading: userLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.userID,
      )({ _id: 1, roles: { name: 1 }, profilDaten: { bankInfos: { iban: 1 } }, useLegacyQuittungen: 1 }),
    [props.userID],
  );

  // the fallback switch is Buchhaltung's tool for investigating a complaint; admins pass through the admin policy
  const { can: canSwitchToLegacy } = useMartinusABAC({ action: MartinusTemporaryActions.VIEW_ACCOUNTING });
  const [legacySavedAt, setLegacySavedAt] = useState(0);

  const {
    value: quittungen,
    loading: quittungenLoading,
    error: quittungenError,
  } = useQuery(
    () => (props.userID ? QuittungenAPI.getForUserID(props.userID)(quittungSelection) : of([])),
    /**
     * The flag decides which receipts the server returns, so flipping it has to refetch. The toggle flips the local
     * value before the save lands, so a fetch in between still gets the old receipts — and the download, which reads
     * the saved flag, then cannot find them. `legacySavedAt` refetches once the flag is persisted.
     */
    [props.userID, user?.useLegacyQuittungen, legacySavedAt],
  );

  const [activeGroupingID, setActiveGroupingID] = useState(groupings[0]?.fieldName ?? 'schoolyear');

  /**
   * We need to differentiate between normal quittungen for parents and external donations
   * External donation get a special template for tax reasons
   */
  const isSpender = computed(() => {
    if (!user) return false;
    return hasRoles({ user, roles: SPENDER_ROLES });
  });

  /**
   * Group Quittungen after the selected key
   */
  const quittungenGrouped = computed(() => {
    if (!quittungen) return;
    const map = new Map<string, NarrowedEntity<Quittung, typeof quittungSelection>[]>();

    const quittungenSorted = [...quittungen].sort((a, b) =>
      a.date && b.date ? b.date.getTime() - a.date.getTime() : -1,
    );
    for (const quittung of quittungenSorted) {
      const key = getGroupKey(activeGroupingID, quittung);
      if (!key) continue;
      const res = map.get(key);
      if (res) {
        res.push(quittung);
      } else {
        map.set(key, [quittung]);
      }
    }
    return map;
  });

  const [iban, setIban] = useState(user?.profilDaten?.bankInfos?.iban, [user]);

  if (userLoading || quittungenLoading || !props.userID) return PageLoader({});
  const userID = props.userID;

  const hasIban = !!user?.profilDaten?.bankInfos?.iban;
  const groups = Array.from(quittungenGrouped.value?.entries() ?? []);

  return html`
    <div class="card-container">
      ${
        hasIban
          ? nothing
          : html`
              <!-- text in the base colour: the soft alert would tint it warning-orange, unreadable on its cream fill -->
              <div role="alert" class="alert alert-warning alert-soft items-start text-base-content">
                <span class="icon-[material-symbols--warning-outline-rounded] size-6 text-warning"></span>
                <div class="flex w-full flex-col gap-3">
                  <div>
                    <h2 class="font-semibold">Keine IBAN hinterlegt</h2>
                    <p class="text-sm">
                      Da in Ihrem Profil keine IBAN hinterlegt ist, können keine Quittungen geladen werden. Sie können
                      Ihre IBAN hier direkt aktualisieren.
                    </p>
                  </div>
                  <div class="flex flex-wrap items-start gap-2">
                    <div class="w-full max-w-md">
                      ${IBANInput({ value: iban, onChanged: setIban, placeholder: 'IBAN eintragen' })}
                    </div>
                    <button
                      type="button"
                      class="btn btn-primary"
                      ?disabled=${!iban}
                      @click=${async () => {
                        await guardDialog(
                          async () => {
                            if (!user?.profilDaten) return;
                            user.profilDaten.bankInfos ??= new BankDaten({});
                            user.profilDaten.bankInfos.iban = iban;
                            await user.save();
                          },
                          { successText: 'IBAN erfolgreich aktualisiert' },
                        );
                      }}
                    >
                      <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
                      Speichern
                    </button>
                  </div>
                </div>
              </div>
            `
      }

      <div class="flex flex-wrap items-center justify-between gap-4">
        ${
          groups.length
            ? TabBar({
                variant: 'box',
                className: 'w-fit',
                items: groupings.map(grouping => ({ id: grouping.fieldName, label: grouping.name })),
                activeID: activeGroupingID,
                onActiveChange: setActiveGroupingID,
              })
            : nothing
        }
        ${
          canSwitchToLegacy && user
            ? ToggleInput({
                label: 'Alte Quittungslogik verwenden (je Buchung statt je bezahltem Vorgang)',
                value: !!user.useLegacyQuittungen,
                onChanged: async checked => {
                  await guardDialog(async () => {
                    user.useLegacyQuittungen = checked;
                    await user.save();
                    setLegacySavedAt(Date.now());
                  });
                },
              })
            : nothing
        }
      </div>

      ${
        quittungenError
          ? html`<div class="card">
              ${ErrorState({
                title: 'Die Quittungen konnten nicht geladen werden',
                description: quittungenError.message,
              })}
            </div>`
          : !groups.length
            ? html`<div class="card">
                ${EmptyState({
                  icon: 'icon-[material-symbols--receipt-long-outline-rounded]',
                  title: 'Es liegen keine Quittungen vor',
                  description: hasIban
                    ? 'Falls Quittungen fehlen sollten, prüfen Sie bitte, ob Ihre IBAN korrekt eingetragen ist, für welche Sie die Quittungen erhalten wollen.'
                    : 'Sobald Ihre IBAN hinterlegt ist, erscheinen Ihre Quittungen hier.',
                })}
              </div>`
            : html`
                ${repeat(
                  groups,
                  ([key]) => key,
                  ([key, groupQuittungen], i) =>
                    Collapse({
                      heading: key,
                      defaultOpen: i === 0,
                      content: html`
                        <div class="flex flex-col gap-2">
                          ${repeat(
                            groupQuittungen,
                            quittung => quittung._id,
                            quittung => DownloadButton({ userID, quittung, isSpender: isSpender.value }),
                          )}
                        </div>
                      `,
                    }),
                )}
                <p class="text-sm text-base-content/70">
                  Falls Quittungen fehlen sollten, prüfen Sie bitte, ob Ihre IBAN korrekt eingetragen ist, für welche
                  Sie die Quittungen erhalten wollen.
                </p>
              `
      }
    </div>
  `;
});
